import React from "react";
import { Repeat2, ShieldCheck, Zap, MessageCircle, CreditCard, KeyRound, FileCheck2, Mail, Smartphone } from "lucide-react";

/**
 * Ecosystem visualization — central Akomoda hub connected to OTAs, payments,
 * communication, fiscal and IoT. Animated signal pulses travel along curved
 * connection lines. Pure SVG + CSS.
 */
const NODES = [
  // [key, label, badge, side, idx (vertical order)]
  { id: "booking", label: "Booking.com", bg: "#003580", mark: <span className="font-display text-[15px] font-extrabold text-white">B<span className="text-[#FEBA02]">.</span></span>, side: "L", idx: 0 },
  { id: "airbnb", label: "Airbnb", bg: "#FF5A5F", mark: (
    <svg viewBox="0 0 32 32" className="h-5 w-5 fill-white" aria-hidden>
      <path d="M16 2c-3 0-5 2.2-6.8 5.4C5.1 14.7 2 21.7 2 25c0 3 2.2 5 5 5 2.4 0 4.7-1.6 9-7 4.3 5.4 6.6 7 9 7 2.8 0 5-2 5-5 0-3.3-3.1-10.3-7.2-17.6C20 4.2 18 2 16 2z" />
    </svg>
  ), side: "L", idx: 1 },
  { id: "expedia", label: "Expedia", bg: "#FFC72C", mark: <span className="font-display text-[12px] font-extrabold text-[#191E3B]">EX</span>, side: "L", idx: 2 },
  { id: "agoda", label: "Agoda", bg: "#5392F9", mark: (
    <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
      <circle cx="14" cy="14" r="9" fill="#fff" />
      <circle cx="20" cy="20" r="4" fill="#FF6F00" />
    </svg>
  ), side: "L", idx: 3 },

  { id: "google", label: "Google Hotels", bg: "#FFFFFF", border: "#E2E8F0", mark: (
    <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
      <path fill="#4285F4" d="M16 14v4.4h6.2c-.3 1.4-1 2.6-2.1 3.4l3.5 2.7c2-1.9 3.2-4.6 3.2-7.8 0-.7-.1-1.5-.2-2.2H16z" />
      <path fill="#34A853" d="M9 18.7l-3.6 2.9C7.2 25.3 11.3 28 16 28c2.7 0 4.9-.9 6.6-2.4l-3.5-2.7c-1 .6-2.2 1-3.1 1-2.4 0-4.5-1.6-5.2-3.8z" />
      <path fill="#FBBC05" d="M5.4 10.4C4.5 12.1 4 14 4 16s.5 3.9 1.4 5.6L9 18.7c-.2-.6-.4-1.3-.4-2s.2-1.4.4-2L5.4 10.4z" />
      <path fill="#EA4335" d="M16 8.9c1.5 0 2.8.5 3.9 1.5l2.9-2.9C20.9 5.7 18.7 4.8 16 4.8c-4.7 0-8.8 2.7-10.6 6.6L9 14.3c.7-2.2 2.8-3.8 5.2-3.8z" />
    </svg>
  ), side: "R", idx: 0 },
  { id: "whatsapp", label: "WhatsApp", desc: "Mesazhet me klientin", bg: "#25D366", mark: <MessageCircle size={18} className="text-white" />, side: "R", idx: 1 },
  { id: "stripe", label: "Stripe", desc: "Pagesa online", bg: "#635BFF", mark: <CreditCard size={18} className="text-white" />, side: "R", idx: 2 },
  { id: "smartlock", label: "Smart Lock", desc: "Hyrje pa çelës", bg: "#0EA5E9", mark: <KeyRound size={18} className="text-white" />, side: "R", idx: 3 },

  // Bottom row — full-width ecosystem extras
  { id: "fiskal", label: "Fiskalizimi", desc: "Albania · Kosovë · MK", bg: "#0F172A", border: "#1E293B", mark: <FileCheck2 size={18} className="text-[#FFB068]" />, side: "B", idx: 0 },
  { id: "email", label: "Email transactional", desc: "Konfirmime automatike", bg: "#FFFFFF", border: "#E2E8F0", mark: <Mail size={18} className="text-[#0F172A]" />, side: "B", idx: 1 },
  { id: "sms", label: "SMS gateway", desc: "Njoftime në kohë reale", bg: "#F1F5F9", border: "#CBD5E1", mark: <Smartphone size={18} className="text-[#0F172A]" />, side: "B", idx: 2 },
];

function NodeChip({ node, align = "left", index = 0 }) {
  return (
    <div
      className={`group relative z-10 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0B1220] p-3 transition-all duration-300 hover:border-white/25 hover:bg-[#111C30] ${align === "right" ? "flex-row-reverse text-right" : ""}`}
      style={{ animation: `float 8s ease-in-out infinite`, animationDelay: `${(index * 0.4).toFixed(2)}s` }}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-lg ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105"
        style={{ background: node.bg, border: node.border ? `1px solid ${node.border}` : undefined }}
      >
        {node.mark}
      </span>
      <div className="min-w-0 leading-tight">
        <div className="truncate text-[13.5px] font-semibold text-white">{node.label}</div>
        <div className={`flex items-center gap-1.5 text-[10.5px] text-white/55 ${align === "right" ? "justify-end" : ""}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-soft" />
          {node.desc || "I lidhur"}
        </div>
      </div>
    </div>
  );
}

export default function IntegrationWall() {
  // Connection paths in % units (viewBox 100×100, stretched to container).
  // Left edge nodes ~x=32% (inner edge of left card column), right ~x=68%, hub at (50,50)
  const paths = [
    // Left side — 4 straight connections
    "M 32 12 L 50 50",
    "M 32 37 L 50 50",
    "M 32 63 L 50 50",
    "M 32 88 L 50 50",
    // Right side — 4 straight connections
    "M 68 12 L 50 50",
    "M 68 37 L 50 50",
    "M 68 63 L 50 50",
    "M 68 88 L 50 50",
  ];

  const left = NODES.filter((n) => n.side === "L");
  const right = NODES.filter((n) => n.side === "R");
  const bottom = NODES.filter((n) => n.side === "B");

  return (
    <div className="relative" data-testid="integration-wall">
      {/* Connection lines + animated pulses */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="line-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#FF7A00" stopOpacity="0.05" />
            <stop offset="0.5" stopColor="#FF7A00" stopOpacity="0.55" />
            <stop offset="1" stopColor="#FF7A00" stopOpacity="0.05" />
          </linearGradient>
          <radialGradient id="pulse-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD8A8" stopOpacity="1" />
            <stop offset="60%" stopColor="#FF7A00" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#FF7A00" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Static lines */}
        {paths.map((d, i) => (
          <path key={`p-${i}`} d={d} stroke="url(#line-grad)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
        ))}

        {/* Traveling signal pulses — staggered per line */}
        {paths.map((d, i) => (
          <g key={`pulse-${i}`}>
            <circle r="0.8" fill="url(#pulse-grad)" vectorEffect="non-scaling-stroke">
              <animateMotion dur={`${4 + (i % 4) * 0.6}s`} repeatCount="indefinite" begin={`${i * 0.45}s`} path={d} />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur={`${4 + (i % 4) * 0.6}s`} repeatCount="indefinite" begin={`${i * 0.45}s`} />
            </circle>
          </g>
        ))}
      </svg>

      <div className="relative grid items-center gap-5 md:grid-cols-[1fr_auto_1fr]">
        {/* Left column */}
        <div className="space-y-2.5">
          {left.map((n, i) => <NodeChip key={n.id} node={n} align="left" index={i} />)}
        </div>

        {/* Center hub — larger, more cinematic */}
        <div className="relative z-10 mx-auto w-full max-w-[290px]">
          <div className="relative">
            {/* Concentric breathing rings */}
            <div className="absolute -inset-12 rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent)] blur-md breathe" aria-hidden />
            <div className="absolute -inset-3 rounded-3xl border border-[#FF7A00]/15" aria-hidden />
            <div className="absolute -inset-1.5 rounded-3xl border border-[#FF7A00]/10" aria-hidden />

            <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-[#0B1220] p-5 ring-glow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="/akomoda-logo-light.png" alt="Akomoda" className="h-7 w-auto" loading="lazy" decoding="async" width="140" height="28" />
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-[10px] font-semibold text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-soft" /> aktiv
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-1.5">
                {[
                  [Repeat2, "Sinkron"],
                  [ShieldCheck, "Pa konflikte"],
                  [Zap, "Real-time"],
                ].map(([Icon, t]) => (
                  <div key={t} className="rounded-md border border-white/10 bg-white/[0.03] p-2 text-center">
                    <Icon size={14} className="mx-auto text-[#FFB068]" />
                    <div className="mt-1 text-[9.5px] font-semibold text-white/75">{t}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 rounded-md border border-white/10 bg-black/30 p-3 text-[10.5px]">
                <div className="flex items-center justify-between">
                  <span className="text-white/55">Përditësimi i fundit</span>
                  <span className="font-mono font-semibold text-[#FFB068]">tani</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-white/55">Kanale aktive</span>
                  <span className="font-mono font-semibold text-white">11 / 11</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-white/55">Rezervime të dyfishta</span>
                  <span className="font-mono font-semibold text-[#22C55E]">0</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-white/55">Sinkron mesatar</span>
                  <span className="font-mono font-semibold text-white">1.4s</span>
                </div>
              </div>

              {/* Throughput meter */}
              <div className="mt-3">
                <div className="flex items-center justify-between text-[9.5px] uppercase tracking-wider text-white/45">
                  <span>Trafiku 24h</span>
                  <span className="text-emerald-300">+18%</span>
                </div>
                <div className="mt-1.5 flex h-6 items-end gap-[3px]">
                  {[36, 52, 44, 58, 70, 62, 78, 84, 76, 88, 92, 80, 86, 94, 90, 96].map((v, i) => (
                    <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-[#FF7A00] to-[#FFB068]" style={{ height: `${v}%`, opacity: 0.55 + (i / 32) }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-2.5">
          {right.map((n, i) => <NodeChip key={n.id} node={n} align="right" index={i + 2} />)}
        </div>
      </div>

      {/* Bottom strip — ecosystem extras (NOT a card grid; treat as a connected lower ring) */}
      <div className="relative mt-6">
        <div className="hairline-dark" />
        <div className="mt-5 grid gap-2.5 md:grid-cols-4">
          {bottom.map((n, i) => <NodeChip key={n.id} node={n} align="left" index={i + 5} />)}
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-2.5 text-[11.5px] text-white/55">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.02] text-white/55">+</span>
            <span>Mbi 30 integrime të tjera përmes API publik</span>
          </div>
        </div>
      </div>
    </div>
  );
}
